/**
 * El método del plato, calculado al leer.
 *
 * Nada de esto se guarda: se deriva cada vez de los eventos en bruto. Cambiar
 * el objetivo de referencia recalcula el histórico entero en lugar de dejarlo
 * congelado en el criterio del día en que se escribió.
 */
import {
  COURSES,
  FOOD_GROUPS,
  INFORMATIVE_GROUPS,
  MEAL_SLOTS,
  PROTEIN_GROUPS,
  eatenDishes,
  gramsOf,
  payloadOf,
  type Course,
  type FoodGroup,
  type HouseholdEvent,
  type MealSlot,
  type PayloadOf,
  type ProteinGroup,
} from './events.js'
import { weekDays, type DayKey, type WeekKey } from './time.js'

/**
 * La proporción de referencia: mitad verdura y fruta, un cuarto proteína, un
 * cuarto cereal. Es el estándar del plato (MyPlate) y se puede cambiar aquí
 * sin tocar un solo dato guardado.
 */
export const PLATE_TARGET = { vegetal: 0.5, proteina: 0.25, cereal: 0.25 } as const

/** Cuánto puede desviarse una porción antes de avisar, en puntos de proporción. */
export const PLATE_TOLERANCE = 0.1

export type PlateBucket = keyof typeof PLATE_TARGET

export const PLATE_BUCKET_LABELS: Record<PlateBucket, string> = {
  vegetal: 'Verdura y fruta',
  proteina: 'Proteína',
  cereal: 'Cereal',
}

/** Qué grupos suman en cada porción del plato. El lácteo y la grasa no suman. */
export const GROUPS_BY_BUCKET: Record<PlateBucket, readonly FoodGroup[]> = {
  vegetal: ['verdura', 'fruta'],
  proteina: PROTEIN_GROUPS,
  cereal: ['cereal'],
}

export type PlateStatus = 'sin-datos' | 'bajo' | 'ok' | 'alto'

export interface DayPlate {
  dayKey: DayKey
  /** Gramos por grupo, incluidos los informativos. */
  grams: Record<FoodGroup, number>
  /** Gramos de cada porción del plato. */
  buckets: Record<PlateBucket, number>
  /** Proporción real de cada porción, de 0 a 1. Cero cuando no hay nada. */
  shares: Record<PlateBucket, number>
  status: Record<PlateBucket, PlateStatus>
  /** Lácteo y grasa: se enseñan, no puntúan. */
  informative: Record<(typeof INFORMATIVE_GROUPS)[number], number>
  /** Platos contados, es decir, los que tenían receta detrás. */
  countedDishes: number
  /** Platos de ese día sin receta: no se pueden contar y hay que decirlo. */
  dishesWithoutRecipe: number
  /** Ingredientes cuyo peso no se conoce, así que no entran en la proporción. */
  unweighedIngredients: number
}

export type RecipePayload = PayloadOf<'recipe'>
export type PlanPayload = PayloadOf<'meal_plan'>
export type EatenPayload = PayloadOf<'meal_eaten'>

/** Lo que manda en una franja, con su evento detrás para poder editarlo. */
export interface SlotEntry<T> {
  event: HouseholdEvent
  payload: T
}

/** Un plato de una comida ya resuelta. */
export interface EffectiveDish {
  course: Course
  recipeId: string | null
  freeText: string
  /** El evento de plan de ese plato, si lo hay, para cambiarlo o quitarlo. */
  planEventId: string | null
}

/** Una comida ya resuelta: lo que se comió si consta, y si no, lo planificado. */
export interface EffectiveMeal {
  dayKey: DayKey
  slot: MealSlot
  /** Sus platos en el orden en que se sirven. */
  dishes: EffectiveDish[]
  /** Verdadero cuando hay registro de haberla comido. */
  eaten: boolean
  skipped: boolean
  /** El registro de lo comido, para deshacerlo desde la interfaz. */
  eatenEventId: string | null
}

function slotKey(dayKey: DayKey, slot: MealSlot): string {
  return `${dayKey}|${slot}`
}

/** La clave de un plato planificado: franja y lugar dentro de ella. */
export function courseKey(dayKey: DayKey, slot: MealSlot, course: Course): string {
  return `${dayKey}|${slot}|${course}`
}

function byCourse(a: { course: Course }, b: { course: Course }): number {
  return COURSES.indexOf(a.course) - COURSES.indexOf(b.course)
}

/** Los eventos vivos de un tipo, ya validados. */
function liveOfType<T extends 'recipe' | 'meal_plan' | 'meal_eaten'>(
  events: readonly HouseholdEvent[],
  type: T,
): Array<{ event: HouseholdEvent; payload: PayloadOf<T> }> {
  const rows: Array<{ event: HouseholdEvent; payload: PayloadOf<T> }> = []
  for (const event of events) {
    if (event.deletedAt || event.type !== type) continue
    const payload = payloadOf(event, type)
    if (payload) rows.push({ event, payload })
  }
  return rows
}

export function recipesById(events: readonly HouseholdEvent[]): Map<string, RecipePayload> {
  const recipes = new Map<string, RecipePayload>()
  for (const { event, payload } of liveOfType(events, 'recipe')) recipes.set(event.id, payload)
  return recipes
}

/**
 * Una sola entrada por franja: la más reciente gana.
 *
 * Dos móviles pueden planificar la misma cena sin saberlo el uno del otro; en
 * vez de rechazar la segunda escritura, las dos se guardan y al leer manda la
 * última. Es la misma regla que resuelve conflictos en la sincronización.
 */
function latestPer<T>(
  rows: Array<{ event: HouseholdEvent; payload: T }>,
  keyOf: (payload: T) => string,
): Map<string, { event: HouseholdEvent; payload: T }> {
  const latest = new Map<string, { event: HouseholdEvent; payload: T }>()
  for (const row of rows) {
    const key = keyOf(row.payload)
    const current = latest.get(key)
    if (!current || row.event.updatedAt > current.event.updatedAt) latest.set(key, row)
  }
  return latest
}

/** El plan, un plato por clave de `courseKey`. */
export function planBySlot(events: readonly HouseholdEvent[]): Map<string, SlotEntry<PlanPayload>> {
  return latestPer(liveOfType(events, 'meal_plan'), (p) => courseKey(p.dayKey, p.slot, p.course))
}

/** Lo comido, uno por franja: el registro lleva todos los platos de esa vez. */
export function eatenBySlot(events: readonly HouseholdEvent[]): Map<string, SlotEntry<EatenPayload>> {
  return latestPer(liveOfType(events, 'meal_eaten'), (p) => slotKey(p.dayKey, p.slot))
}

/**
 * Todos los eventos vivos de plan de un plato, no sólo el que manda.
 *
 * Si dos móviles pusieron la misma guarnición sin cobertura hay dos; quitar
 * sólo el ganador haría reaparecer el otro, así que quitar mira todos.
 */
export function planEventsAt(
  events: readonly HouseholdEvent[],
  dayKey: DayKey,
  slot: MealSlot,
  course: Course,
): HouseholdEvent[] {
  return liveOfType(events, 'meal_plan')
    .filter(({ payload }) => payload.dayKey === dayKey && payload.slot === slot && payload.course === course)
    .map(({ event }) => event)
}

/** Los platos planificados de una franja, en el orden en que se sirven. */
export function plannedDishes(
  plan: Map<string, SlotEntry<PlanPayload>>,
  dayKey: DayKey,
  slot: MealSlot,
): EffectiveDish[] {
  const dishes: EffectiveDish[] = []
  for (const course of COURSES) {
    const entry = plan.get(courseKey(dayKey, slot, course))
    if (!entry) continue
    dishes.push({
      course,
      recipeId: entry.payload.recipeId,
      freeText: entry.payload.freeText,
      planEventId: entry.event.id,
    })
  }
  return dishes
}

/**
 * Lo que de verdad hubo en cada franja de un día.
 *
 * Lo comido manda sobre lo planificado: el plan es una intención y lo comido
 * es un hecho. Mientras no conste lo segundo, el plan es la mejor estimación
 * de cómo va a quedar el día, que es lo que permite corregir la cena a tiempo.
 */
export function effectiveMealsOfDay(
  dayKey: DayKey,
  events: readonly HouseholdEvent[],
): EffectiveMeal[] {
  const plan = planBySlot(events)
  const eaten = eatenBySlot(events)
  const meals: EffectiveMeal[] = []

  for (const slot of MEAL_SLOTS) {
    const actual = eaten.get(slotKey(dayKey, slot))
    const planned = plannedDishes(plan, dayKey, slot)
    if (!actual && planned.length === 0) continue

    const dishes = actual
      ? eatenDishes(actual.payload)
          .map((dish) => ({
            ...dish,
            planEventId: planned.find((p) => p.course === dish.course)?.planEventId ?? null,
          }))
          .sort(byCourse)
      : planned

    meals.push({
      dayKey,
      slot,
      dishes: actual?.payload.skipped ? [] : dishes,
      eaten: Boolean(actual),
      skipped: actual ? actual.payload.skipped : false,
      eatenEventId: actual?.event.id ?? null,
    })
  }
  return meals
}

function emptyGrams(): Record<FoodGroup, number> {
  return Object.fromEntries(FOOD_GROUPS.map((group) => [group, 0])) as Record<FoodGroup, number>
}

/** El plato de un día: gramos por grupo, proporciones y en qué se desvía. */
export function dayPlate(dayKey: DayKey, events: readonly HouseholdEvent[]): DayPlate {
  const recipes = recipesById(events)
  const meals = effectiveMealsOfDay(dayKey, events)
  const grams = emptyGrams()

  let countedDishes = 0
  let dishesWithoutRecipe = 0
  let unweighedIngredients = 0

  for (const dish of meals.flatMap((meal) => meal.dishes)) {
    const recipe = dish.recipeId ? recipes.get(dish.recipeId) : undefined
    if (!recipe) {
      dishesWithoutRecipe += 1
      continue
    }
    countedDishes += 1
    for (const ingredient of recipe.ingredients) {
      const weight = gramsOf(ingredient)
      if (!ingredient.group || weight === null) {
        unweighedIngredients += 1
        continue
      }
      grams[ingredient.group] += weight
    }
  }

  const buckets = {
    vegetal: sumGroups(grams, GROUPS_BY_BUCKET.vegetal),
    proteina: sumGroups(grams, GROUPS_BY_BUCKET.proteina),
    cereal: sumGroups(grams, GROUPS_BY_BUCKET.cereal),
  }
  const scored = buckets.vegetal + buckets.proteina + buckets.cereal

  const shares = {
    vegetal: scored > 0 ? buckets.vegetal / scored : 0,
    proteina: scored > 0 ? buckets.proteina / scored : 0,
    cereal: scored > 0 ? buckets.cereal / scored : 0,
  }

  return {
    dayKey,
    grams,
    buckets,
    shares,
    status: {
      vegetal: statusOf(shares.vegetal, PLATE_TARGET.vegetal, scored),
      proteina: statusOf(shares.proteina, PLATE_TARGET.proteina, scored),
      cereal: statusOf(shares.cereal, PLATE_TARGET.cereal, scored),
    },
    informative: { lacteo: grams.lacteo, grasa: grams.grasa },
    countedDishes,
    dishesWithoutRecipe,
    unweighedIngredients,
  }
}

function sumGroups(grams: Record<FoodGroup, number>, groups: readonly FoodGroup[]): number {
  return groups.reduce((total, group) => total + grams[group], 0)
}

function statusOf(share: number, target: number, scored: number): PlateStatus {
  if (scored <= 0) return 'sin-datos'
  if (share < target - PLATE_TOLERANCE) return 'bajo'
  if (share > target + PLATE_TOLERANCE) return 'alto'
  return 'ok'
}

export interface ProteinVariety {
  weekKey: WeekKey
  /** Comidas de la semana que incluyeron ese tipo de proteína. */
  meals: Record<ProteinGroup, number>
  /** Días distintos en que apareció; es lo que mide la variedad de verdad. */
  days: Record<ProteinGroup, number>
  /** Los que no aparecen ni una vez en toda la semana. */
  missing: ProteinGroup[]
}

/**
 * La variedad de proteína de una semana.
 *
 * Es descriptiva a propósito: cuenta lo que hubo y señala lo que falta, sin
 * inventarse cuántas veces "hay que" comer pescado. La proporción de proteína
 * dentro del día ya la vigila el plato; esto mira de qué fue esa proteína.
 */
export function proteinVariety(
  weekKey: WeekKey,
  events: readonly HouseholdEvent[],
): ProteinVariety {
  const recipes = recipesById(events)
  const meals = Object.fromEntries(PROTEIN_GROUPS.map((g) => [g, 0])) as Record<ProteinGroup, number>
  const days = Object.fromEntries(PROTEIN_GROUPS.map((g) => [g, 0])) as Record<ProteinGroup, number>

  for (const dayKey of weekDays(weekKey)) {
    const seenToday = new Set<ProteinGroup>()
    for (const meal of effectiveMealsOfDay(dayKey, events)) {
      const inThisMeal = new Set<ProteinGroup>()
      for (const dish of meal.dishes) {
        const recipe = dish.recipeId ? recipes.get(dish.recipeId) : undefined
        for (const ingredient of recipe?.ingredients ?? []) {
          const group = ingredient.group
          if (group && (PROTEIN_GROUPS as readonly string[]).includes(group)) {
            inThisMeal.add(group as ProteinGroup)
          }
        }
      }
      for (const group of inThisMeal) {
        meals[group] += 1
        seenToday.add(group)
      }
    }
    for (const group of seenToday) days[group] += 1
  }

  return {
    weekKey,
    meals,
    days,
    missing: PROTEIN_GROUPS.filter((group) => meals[group] === 0),
  }
}
